import React from "react";
import { Search, Inbox, Eye, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/context/LanguageContext";
import { fmtDate } from "@/lib/formatters";
import { StatusBadge, LEAD_STATUSES } from "@/components/admin/StatusBadge";

const ALL = "all";

const EmptyLeads = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="admin-leads-empty">
    <Inbox className="h-10 w-10 text-[var(--muted-ink)]" />
    <p className="mt-3 text-sm text-[var(--muted-ink)]">{message}</p>
  </div>
);

const LeadRow = ({ lead, onView }) => (
  <tr className="border-b border-[color:var(--border-subtle)] last:border-0 transition-colors hover:bg-[var(--surface-2)]" data-testid={`lead-row-${lead.id}`}>
    <td className="px-4 py-3 font-semibold text-[var(--brand-navy)]">
      {lead.name}
      <span className="ml-2 rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-medium uppercase text-[var(--muted-ink)]">{lead.language}</span>
    </td>
    <td className="px-4 py-3"><StatusBadge status={lead.status} testId={`lead-status-${lead.id}`} /></td>
    <td className="px-4 py-3 text-[var(--muted-ink)]"><div>{lead.phone}</div><div className="text-xs">{lead.email}</div></td>
    <td className="hidden max-w-xs px-4 py-3 text-[var(--muted-ink)] md:table-cell"><span className="line-clamp-2">{lead.message}</span></td>
    <td className="px-4 py-3 text-center">
      <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[rgba(139,30,63,0.08)] px-1.5 text-xs font-bold text-[var(--brand-burgundy)]">{lead.document_count || 0}</span>
    </td>
    <td className="hidden px-4 py-3 text-xs text-[var(--muted-ink)] lg:table-cell">{fmtDate(lead.created_at)}</td>
    <td className="px-4 py-3 text-right">
      <Button variant="ghost" size="sm" onClick={() => onView(lead)} className="h-8 rounded-lg" data-testid={`view-lead-${lead.id}`}>
        <Eye className="h-4 w-4" />
      </Button>
    </td>
  </tr>
);

export const LeadsTable = ({
  leads, search, onSearchChange, onSearchSubmit,
  statusFilter, onStatusFilterChange, onView,
}) => {
  const { t } = useLanguage();
  const submit = (e) => { e.preventDefault(); onSearchSubmit(); };
  const filtered = !!statusFilter;

  return (
    <>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <form onSubmit={submit} className="flex flex-1 items-center gap-2 sm:max-w-md">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-ink)]" />
            <Input value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder={t.admin.search} className="h-10 rounded-xl pl-9" data-testid="admin-search" />
          </div>
          <Button type="submit" className="h-10 rounded-xl bg-[var(--brand-burgundy)] px-4 font-semibold text-white hover:bg-[var(--brand-burgundy-dark)]">{t.admin.search.replace("\u2026", "")}</Button>
        </form>

        <Select value={statusFilter || ALL} onValueChange={(v) => onStatusFilterChange(v === ALL ? "" : v)}>
          <SelectTrigger className="h-10 w-full rounded-xl bg-white sm:w-[200px]" aria-label={t.admin.statusLabel} data-testid="status-filter">
            <span className="flex items-center gap-2 truncate">
              <Filter className="h-4 w-4 shrink-0 text-[var(--muted-ink)]" />
              <SelectValue />
            </span>
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value={ALL} data-testid="status-filter-all">{t.admin.statusAll}</SelectItem>
            {LEAD_STATUSES.map((s) => (
              <SelectItem key={s} value={s} data-testid={`status-filter-${s}`}>{t.admin.statuses[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[color:var(--border-subtle)] bg-white shadow-[var(--shadow-sm)]">
        {leads.length === 0 ? (
          <EmptyLeads message={filtered ? t.admin.noStatusResults : t.admin.noLeads} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="border-b border-[color:var(--border-subtle)] bg-[var(--surface-2)] text-xs uppercase tracking-wide text-[var(--muted-ink)]">
                  <th className="px-4 py-3 font-semibold">{t.admin.colName}</th>
                  <th className="px-4 py-3 font-semibold">{t.admin.colStatus}</th>
                  <th className="px-4 py-3 font-semibold">{t.admin.colContact}</th>
                  <th className="hidden px-4 py-3 font-semibold md:table-cell">{t.admin.colMessage}</th>
                  <th className="px-4 py-3 text-center font-semibold">{t.admin.colDocs}</th>
                  <th className="hidden px-4 py-3 font-semibold lg:table-cell">{t.admin.colDate}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => <LeadRow key={l.id} lead={l} onView={onView} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};
